import type { CommandResult, FeedbackReport, UUID } from '../../domain/types';
import { nowISO } from '../../lib/time';
import { uuid } from '../../lib/uuid';
import type { FeedbackRepository, ReportInput } from '../types';
import { MockDb } from './MockDb';

/**
 * Faults, ideas and questions on their way to the administrators.
 *
 * Deliberately the easiest thing in the app to do. There is no form to find, no
 * category to choose and no field to fill in: somebody tells the assistant that
 * something is broken, and the assistant writes it down with everything it
 * already knows about where they were standing. The whole point is that the
 * report arrives with the context a person would never have thought to include
 * and could not have described anyway — which screen, which mode, offline or
 * not, what the app last said had gone wrong.
 *
 * Reports are never deleted, only answered and closed, like everything else here.
 */
export class MockFeedbackRepository implements FeedbackRepository {
  constructor(private db: MockDb) {}

  private canSeeAll(): boolean {
    const role = this.db.getSession()?.role;
    return role === 'editor' || role === 'admin';
  }

  async list(status?: FeedbackReport['status']): Promise<FeedbackReport[]> {
    await this.db.read();
    if (!this.canSeeAll()) return [];
    return this.db.t.feedback_reports
      .filter((r) => !status || r.status === status)
      .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  }

  async listMine(): Promise<FeedbackReport[]> {
    await this.db.read();
    const me = this.db.getSession()?.accountId;
    if (!me) return [];
    return this.db.t.feedback_reports.filter((r) => r.accountId === me).sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  }

  async countNew(): Promise<number> {
    await this.db.ready;
    if (!this.canSeeAll()) return 0;
    return this.db.t.feedback_reports.filter((r) => r.status === 'new').length;
  }

  async send(input: ReportInput): Promise<CommandResult<FeedbackReport>> {
    // Sent before the write, so an offline report is still worth something: the
    // usual queue would drop it, and "I told you and nothing happened" is worse
    // than a slightly stale note.
    await this.db.ready;
    const session = this.db.getSession();
    if (!session) return { ok: false, kind: 'permission', messageKey: 'error.signedOut' };

    const already = this.db.idem(input.idempotencyKey);
    if (already) {
      const row = this.db.t.feedback_reports.find((r) => r.id === already.entityId);
      if (row) return { ok: true, value: row, noop: true };
    }
    if (!input.title.trim()) return { ok: false, kind: 'validation', messageKey: 'assistant.report.needTitle' };

    const now = nowISO();
    const report: FeedbackReport = {
      id: uuid(),
      familyId: this.db.familyId(),
      kind: input.kind,
      title: input.title.trim().slice(0, 200),
      detail: input.detail?.trim().slice(0, 4000) || undefined,
      status: 'new',
      accountId: session.accountId,
      personId: session.personId ?? undefined,
      context: input.context,
      // Only the tail of the exchange: enough to see what was asked, not a transcript.
      conversation: input.conversation?.slice(-6).map((m) => ({ role: m.role, text: m.text.slice(0, 1000) })),
      createdAt: now,
      updatedAt: now,
    };
    this.db.t.feedback_reports.push(report);
    this.db.rememberIdem(input.idempotencyKey, 'feedback_report', report.id);

    const from = this.db.accountName(session.accountId);
    for (const adminId of this.db.adminAccountIds()) {
      if (adminId === session.accountId) continue;
      this.db.notify({
        accountId: adminId,
        kind: 'report',
        title: input.kind === 'idea' ? 'An idea from the family' : input.kind === 'question' ? 'A question from the family' : 'Something is not working',
        titleHe: input.kind === 'idea' ? 'רעיון מהמשפחה' : input.kind === 'question' ? 'שאלה מהמשפחה' : 'משהו לא עובד',
        body: `${from}: ${report.title}`,
        bodyHe: `${this.db.accountName(session.accountId, 'he')}: ${report.title}`,
        link: '/manage/reports',
        actorAccountId: session.accountId,
      });
    }
    this.db.audit({
      actorAccountId: session.accountId,
      entityType: 'feedback_report',
      entityId: report.id,
      action: 'create',
      description: `Sent a ${input.kind}: ${report.title}`,
    });
    await this.db.commit(['feedback_reports', 'notifications', 'audit_events', 'idempotency'], [report.id]);
    return { ok: true, value: report };
  }

  async setStatus(id: UUID, status: FeedbackReport['status']): Promise<CommandResult<FeedbackReport>> {
    await this.db.write();
    if (!this.canSeeAll()) return { ok: false, kind: 'permission', messageKey: 'error.editorOnly' };
    const row = this.db.t.feedback_reports.find((r) => r.id === id);
    if (!row) return { ok: false, kind: 'not_found', messageKey: 'error.notFound' };
    row.status = status;
    row.updatedAt = nowISO();
    this.db.audit({
      actorAccountId: this.db.requireSession().accountId,
      entityType: 'feedback_report',
      entityId: id,
      action: 'update',
      after: { status },
      description: `Marked a report ${status}`,
    });
    await this.db.commit(['feedback_reports', 'audit_events'], [id]);
    return { ok: true, value: row };
  }

  async answer(id: UUID, answer: string): Promise<CommandResult<FeedbackReport>> {
    await this.db.write();
    if (!this.canSeeAll()) return { ok: false, kind: 'permission', messageKey: 'error.editorOnly' };
    const row = this.db.t.feedback_reports.find((r) => r.id === id);
    if (!row) return { ok: false, kind: 'not_found', messageKey: 'error.notFound' };
    const session = this.db.requireSession();
    row.answer = answer.trim().slice(0, 2000);
    row.answeredBy = session.accountId;
    row.answeredAt = nowISO();
    row.updatedAt = row.answeredAt;
    if (row.status === 'new') row.status = 'seen';
    // The person who sent it hears back. Without this a report is a suggestion
    // box nailed shut, and people stop using it.
    this.db.notify({
      accountId: row.accountId,
      kind: 'report',
      title: 'An answer about what you sent',
      titleHe: 'תשובה על מה ששלחת',
      body: `${row.title} — ${row.answer}`,
      bodyHe: `${row.title} — ${row.answer}`,
      actorAccountId: session.accountId,
    });
    this.db.audit({
      actorAccountId: session.accountId,
      entityType: 'feedback_report',
      entityId: id,
      action: 'update',
      description: `Answered a report: ${row.title}`,
    });
    await this.db.commit(['feedback_reports', 'notifications', 'audit_events'], [id]);
    return { ok: true, value: row };
  }
}
